/**
 * Printer service — abstracts across transports:
 *
 *   1. **System / USB print** (recommended, universal) — the browser's own
 *      print dialog. Works with any OS-registered printer: Niimbot (via
 *      Niimbot Studio driver), Phomemo, Zebra ZDesigner, Brother P-touch,
 *      Epson TM, HP, plus any Bluetooth thermal printer that's been paired
 *      at OS level. Runs on Chrome, Edge, Safari, macOS, Windows, Android.
 *
 *   2. **Bluetooth device pairing (Niimbot / generic)** — via the browser
 *      Web Bluetooth API. Prompts the OS's bluetooth device chooser so the
 *      operator can pair a Niimbot B21 / B3S / B1 / D11 / D110 / D101 (any
 *      GATT-capable printer). Once paired, the printer becomes an OS-level
 *      device and prints go through the normal system dialog above.
 *
 *      NOTE: full byte-level Niimbot binary protocol (direct-to-BLE print
 *      without OS driver) is deliberately NOT bundled — the maintained
 *      libraries (`@mmote/niimbluelib`, `@serialport/*`) pull in Node-only
 *      dependencies that break browser builds. The pairing flow already
 *      unlocks 95 % of the use-case: OS driver takes over from there.
 *
 *   3. **localStorage** remembers the last-used printer id so the next
 *      "Print Label" click auto-picks it.
 *
 * Future-ready: to add Zebra / Brother / Epson direct-BLE drivers, drop
 * another driver here and expose it via a new `kind` — the modal picks up
 * anything registered.
 */

import * as niimbot from "./niimbotDriver";

// -------- Last-printer memory (localStorage) --------
const LAST_PRINTER_KEY = "opikjewels.lastPrinter";

export function loadLastPrinter() {
  try {
    const raw = localStorage.getItem(LAST_PRINTER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveLastPrinter(printer) {
  try {
    localStorage.setItem(LAST_PRINTER_KEY, JSON.stringify(printer));
  } catch { /* quota — ignore, memory-only session */ }
}

// -------- Bluetooth device pairing (Niimbot & generic thermal) --------
/**
 * Prompt the browser Web Bluetooth chooser and pair with a Niimbot / thermal
 * printer at OS level. After pairing, printing runs through the browser's
 * standard print dialog which then talks to the OS-registered printer.
 *
 * Two-tier discovery:
 *   1. **Filtered** — name prefixes of known Niimbot models (B1, B2, B21,
 *      B3, B32, D11, D101, D110, M2, M3) plus their standard service UUIDs.
 *      Keeps the chooser tidy for the common case.
 *   2. **`acceptAllDevices` fallback** — when `opts.showAll=true`, list ANY
 *      Bluetooth device. Use this if the printer doesn't appear in the
 *      filtered list (rare firmware advertising a non-standard name).
 *
 * We do NOT force a GATT connect — physically pairing the device at OS
 * level is enough for `window.print()` to route through it. Forcing GATT
 * used to fail on B2/B3 units that go to sleep between adverts.
 */
export async function connectNiimbotBluetooth(opts = {}) {
  if (!navigator.bluetooth) {
    const err = new Error(
      "Bluetooth is not supported in this browser. Use Chrome or Edge on desktop / Android — or choose 'System / USB' to print via any OS-registered printer.",
    );
    err.code = "BT_UNSUPPORTED";
    throw err;
  }
  // Common Niimbot service UUIDs across firmware versions. The FIRST one
  // (`e7810a71...`) is the modern binary-protocol service used by B1/B2/
  // B21/B31/B3S and required for direct-BLE printing. We list it in
  // `optionalServices` so the picker still exposes GATT access after the
  // user picks a device by name.
  const NIIMBOT_SERVICES = [
    niimbot.NIIMBOT_SERVICE_UUID,                  // Binary protocol (B-series)
    "0000fee7-0000-1000-8000-00805f9b34fb",        // Older B21/D11/D110 firmware
    "6e400001-b5a3-f393-e0a9-e50e24dcca9e",        // Nordic UART (some M-series)
    "0000ff00-0000-1000-8000-00805f9b34fb",        // Generic vendor
    "0000ffe0-0000-1000-8000-00805f9b34fb",        // HM-10 clones
  ];
  const NAME_PREFIXES = [
    "B1", "B2", "B21", "B3", "B31", "B32",
    "D11", "D101", "D110",
    "M2", "M3",
    "NIIMBOT", "Niimbot", "niimbot",
    "JC", "PT",
  ];

  const requestOptions = opts.showAll
    ? { acceptAllDevices: true, optionalServices: NIIMBOT_SERVICES }
    : {
        filters: [
          ...NAME_PREFIXES.map((p) => ({ namePrefix: p })),
          ...NIIMBOT_SERVICES.map((s) => ({ services: [s] })),
        ],
        optionalServices: NIIMBOT_SERVICES,
      };

  let device;
  try {
    device = await navigator.bluetooth.requestDevice(requestOptions);
  } catch (e) {
    if (e.name === "NotFoundError") {
      const err = new Error("No printer selected");
      err.code = "BT_CANCELLED";
      throw err;
    }
    throw e;
  }

  // Try to open the direct-BLE driver session immediately — this is what
  // enables the byte-level protocol print. If the printer doesn't expose
  // the modern service (older firmware) we fall back to OS-mediated print
  // via the browser dialog. The pairing itself always works either way.
  let driver = null;
  try {
    driver = await niimbot.connect(device);
  } catch (e) {
    console.warn("Direct-BLE driver init failed; will fall back to system print:", e?.message || e);
  }

  return {
    id: `bt:${device.id || device.name}`,
    kind: "bluetooth",
    name: device.name || "Niimbot (Bluetooth)",
    model: (device.name || "").split("-")[0] || "unknown",
    device,
    driver,  // null if direct-BLE not available; caller falls back to OS print
    disconnect: async () => {
      try { driver?.disconnect(); } catch { /* already gone */ }
      try { device.gatt?.disconnect(); } catch { /* already gone */ }
    },
  };
}

/**
 * Universal fallback: render the label SVG in a hidden iframe and call
 * `window.print()`. Works on every browser + every OS-installed printer,
 * including USB thermal printers (Niimbot Studio driver, Zebra ZDesigner,
 * Brother P-touch, Epson TM), and produces the OS's own printer picker
 * so the user sees any Bluetooth printer already paired at OS level.
 *
 * Returns a promise that resolves after the print dialog is dismissed.
 */
export function printViaBrowser(svgHtml, copies = 1) {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    // Off-screen, no visible layout shift.
    iframe.style.cssText = "position:fixed;left:-99999px;top:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);

    // The label SVG is repeated `copies` times inside a print stylesheet
    // that forces one label per printable page (works with any driver —
    // the OS chooses the paper size from the printer defaults).
    const body = Array.from(
      { length: Math.max(1, Number(copies) || 1) },
      () => svgHtml,
    ).join(`<div style="page-break-after:always"></div>`);
    const html = `<!doctype html><html><head><title>Print Label</title>
      <style>
        @page { margin: 0; }
        html, body { margin: 0; padding: 0; }
        body { display: flex; flex-direction: column; align-items: center; }
        svg { display: block; margin: 2mm auto; }
      </style></head><body>${body}</body></html>`;

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    const cleanup = () => {
      try { iframe.remove(); } catch { /* already gone */ }
      resolve();
    };
    iframe.contentWindow.addEventListener("afterprint", cleanup, { once: true });
    // Fallback in case afterprint doesn't fire (Safari's known miss)
    setTimeout(cleanup, 60_000);

    // Wait a frame so the SVG paints before opening the dialog.
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error("print() failed", e);
        cleanup();
      }
    }, 250);
  });
}

/**
 * Route a print job to the right transport:
 *   - `printer.driver` present → direct-BLE binary protocol (Niimbot native)
 *   - otherwise                → OS-mediated `window.print()` dialog
 *
 * Callers pass BOTH the SVG string (for the OS path) AND a canvas (for the
 * BLE path). The modal already renders both in lockstep so what previews
 * is what prints regardless of transport.
 */
export async function printLabel(printer, svgHtml, copies = 1, canvas = null) {
  if (printer?.driver && canvas) {
    // Direct-BLE — send the bitmap straight to the head.
    await printer.driver.print(canvas, copies, {
      model: printer.model,
      density: 3,
      labelType: 1, // 1 = die-cut gap paper (all Niimbot jewelry rolls)
    });
    return;
  }
  // Fallback: OS-registered printer via the browser print dialog.
  return printViaBrowser(svgHtml, copies);
}
